import React, { useState, useCallback, ReactNode, createContext, useContext } from 'react'

import without from 'lodash/without'

export interface Alert {
  type: 'success' | 'error' | 'warning' | 'info'
  message: ReactNode
  unique?: boolean
  permanent?: boolean
}

type ContextType = {
  alerts: Alert[]
  addAlert: React.Dispatch<Alert>
  removeAlert: React.Dispatch<Alert>
  reset: React.Dispatch<void>
}

type ProviderProps = {
  children: React.ReactNode
}

const appNotificationsContext = createContext<ContextType | undefined>(undefined)

function useAppNotifications() {
  const context = useContext(appNotificationsContext)
  if (!context) {
    throw new Error(`appNotificationsContext must be used within an AppNotificationsProvider`)
  }
  return context
}

/**
 * Using the appNotificationsContext enables api hooks further down the tree to
 * trigger an App Alert, which will typically be shown at the top level of the app
 */
const AppNotificationsProvider = ({ children }: ProviderProps) => {
  const [alerts, setAlerts] = useState<Alert[]>([])

  const addAlert = useCallback(
    (newAlert: any) => {
      setAlerts((current) => [...current, newAlert])
    },
    [setAlerts],
  )

  const removeAlert = useCallback((alert: any) => setAlerts((current) => without(current, alert)), [setAlerts])

  const reset = useCallback(() => setAlerts([]), [setAlerts])

  const value: ContextType = { alerts, addAlert, removeAlert, reset }

  return <appNotificationsContext.Provider value={value}>{children}</appNotificationsContext.Provider>
}

export { AppNotificationsProvider, useAppNotifications }
